// The right-hand pane of the admin/partner Messages page: who the open conversation is with, plus
// the message templates (when the viewer can use them). Hidden under 1100px by CSS; the Templates
// pill in the composer covers narrow widths.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AddToListMenu from '../search/AddToListMenu';
import { previewString } from './TemplateEditor';
import { friendlyRoleLabel } from './roleLabels';

const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export function TemplatesPanel({ onInsert, onManage, onNew, reloadKey }) {
  const { API_URL } = useAuth();
  const [templates, setTemplates] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/coach-templates/`, cfg());
      setTemplates(res.data.templates || []); setFailed(false);
    } catch { setTemplates([]); setFailed(true); }
  }, [API_URL]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const recent = (t) => (t.last_used_at ? new Date(t.last_used_at).getTime() : -Infinity);
  const sorted = (templates || []).slice().sort((a, b) => recent(b) - recent(a));

  return (
    <div className="msg-templates">
      <div className="msg-templates__head">
        <span className="srch-label">TEMPLATES</span>
        <button type="button" className="srch-link" onClick={onNew}>+ New</button>
      </div>
      {templates === null && <p className="msg-templates__note">Loading…</p>}
      {failed && <p className="msg-templates__note msg-templates__note--err" role="alert">Could not load templates.</p>}
      {templates && !failed && sorted.length === 0 && <p className="msg-templates__note">No templates yet.</p>}
      {sorted.slice(0, 6).map((t) => (
        <button key={t.id} type="button" className="msg-template" onClick={() => onInsert(t)} title={`Insert "${t.name}"`}>
          <span className="msg-template__name">{t.name}</span>
          <span className="msg-template__prev">{previewString(t.parts) || '—'}</span>
        </button>
      ))}
      <button type="button" className="srch-link msg-templates__manage" onClick={onManage}>Manage templates →</button>
    </div>
  );
}

export default function ConversationContext({ other, isAthlete, canOpenProfile, onOpenProfile, templates }) {
  const { API_URL } = useAuth();
  const [profile, setProfile] = useState(null);

  const otherId = other?.id;
  useEffect(() => {
    setProfile(null);
    if (!otherId || !isAthlete || !canOpenProfile) return undefined;
    let cancelled = false;
    // A partner asking about an athlete that is not theirs gets a 404: show the basic card instead.
    axios.get(`${API_URL}/athletes/${otherId}/profile`, cfg())
      .then((r) => { if (!cancelled) setProfile(r.data || null); })
      .catch(() => { if (!cancelled) setProfile(null); });
    return () => { cancelled = true; };
  }, [API_URL, otherId, isAthlete, canOpenProfile]);

  const facts = profile ? [profile.position_primary, profile.graduation_year, profile.school ?? profile.high_school].filter(Boolean).join(' · ') : '';
  const pwr = profile?.power_score != null ? (profile.power_score.value ?? profile.power_score) : null;

  return (
    <aside className="msg-ctx" aria-label="Conversation details">
      {other ? (
        <div className="adm__card msg-ctx__card">
          <span className="msg-ctx__avatar" aria-hidden="true">{initials(other.name)}</span>
          <div className="msg-ctx__name">{other.name}</div>
          <div className="msg-ctx__sub">{facts || friendlyRoleLabel(other.role)}</div>
          <div className="msg-ctx__chips">
            {!isAthlete && <span className="adm-chip">{friendlyRoleLabel(other.role)}</span>}
            {typeof pwr === 'number' && <span className="adm-chip adm-chip--green">{pwr} PWR</span>}
            {profile?.user?.is_concierge && <span className="adm-chip adm-chip--amber">CONCIERGE</span>}
          </div>
          {canOpenProfile && (
            <div className="msg-ctx__actions">
              <button type="button" className="adm-btn adm-btn--dark adm-btn--sm" onClick={onOpenProfile}>Open profile</button>
              {isAthlete && <AddToListMenu athlete={{ user_id: other.id, name: other.name }} />}
            </div>
          )}
        </div>
      ) : (
        <div className="adm__card msg-ctx__card"><p className="msg-templates__note">Select a conversation to see details.</p></div>
      )}
      {templates && <div className="adm__card msg-ctx__card">{templates}</div>}
    </aside>
  );
}
